import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const AuthContext = createContext();

let csrfTokenMemory = null; // Store in memory, not localStorage, for security

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const stored = localStorage.getItem('user');
        return stored ? JSON.parse(stored) : null;
    });

    // Fetch initial CSRF token on mount
    useEffect(() => {
        fetch(`${API_URL}/api/auth/csrf`, { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                if (data.csrfToken) csrfTokenMemory = data.csrfToken;
            })
            .catch(console.error);
    }, []);

    const login = async (username, password) => {
        try {
            // Ensure we have a CSRF token before attempting login, just in case the initial mount fetch didn't finish
            let currentCsrf = csrfTokenMemory;
            if (!currentCsrf) {
                const csrfRes = await fetch(`${API_URL}/api/auth/csrf`, { credentials: 'include' });
                const csrfData = await csrfRes.json();
                if (csrfData.csrfToken) {
                    currentCsrf = csrfData.csrfToken;
                    csrfTokenMemory = currentCsrf;
                }
            }

            const res = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(currentCsrf ? { 'X-XSRF-TOKEN': currentCsrf } : {})
                },
                body: JSON.stringify({ username, password }),
                credentials: 'include',
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || 'Login failed');
            }

            const data = await res.json();
            if (!data.user) {
                throw new Error('Invalid login response');
            }

            setUser(data.user);
            localStorage.setItem('user', JSON.stringify(data.user));

            if (data.csrfToken) {
                csrfTokenMemory = data.csrfToken;
            }

            // Store token for mobile browsers where cookies are blocked (Safari ITP)
            if (data.token) {
                localStorage.setItem('token', data.token);
            }

            return true;
        } catch (err) {
            console.error('❌ Login error:', err.message);
            alert('Login failed: ' + err.message);
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setUser(null);
            return false;
        }
    };

    const logout = useCallback(async () => {
        try {
            await fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            setUser(null);
            localStorage.removeItem('user');
            localStorage.removeItem('token');
        }
    }, []);

    // Send token as Authorization header — cookie is preferred by the server but
    // mobile Safari's ITP blocks cross-origin cookies, so the header acts as fallback.
    // Auto-logout on 401 so expired tokens don't leave the user in a broken state.
    const authorizedFetch = useCallback(async (url, options = {}) => {
        const headers = {
            ...(options.headers || {}),
        };

        if (!headers['Content-Type'] && !headers['content-type'] && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const storedToken = localStorage.getItem('token');
        if (storedToken && !headers['Authorization']) {
            headers['Authorization'] = `Bearer ${storedToken}`;
        }

        if (csrfTokenMemory && !headers['X-XSRF-TOKEN']) {
            headers['X-XSRF-TOKEN'] = csrfTokenMemory;
        }

        let response = await fetch(url, {
            ...options,
            credentials: 'include',
            headers,
        });

        if (response.status === 401) {
            // Token expired, attempt to refresh
            try {
                const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: csrfTokenMemory ? { 'X-XSRF-TOKEN': csrfTokenMemory } : {}
                });

                if (!refreshRes.ok) throw new Error('Refresh failed');

                const refreshData = await refreshRes.json();
                
                // Update local storage fallback
                if (refreshData.token) {
                    localStorage.setItem('token', refreshData.token);
                    headers['Authorization'] = `Bearer ${refreshData.token}`;
                }

                // Retry original request
                response = await fetch(url, {
                    ...options,
                    credentials: 'include',
                    headers,
                });
                
                if (response.status === 401) {
                    logout(); // Refresh failed on second attempt
                }
            } catch (err) {
                logout(); // Refresh token expired or invalid
            }
        }

        return response;
    }, [logout]);

    return (
        <AuthContext.Provider value={{ user, login, logout, authorizedFetch, role: user?.role }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);